import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
    Phone, ArrowRight, CheckCircle2, Gauge, Droplets, Leaf, SunSnow,
    Ruler, ClipboardCheck, HeartHandshake, Home as HomeIcon, House,
    Building2, Warehouse, KeyRound, TreePine,
} from "lucide-react";
import { COMPANY, IMAGES } from "@/lib/site";
import { SEO } from "@/components/SEO";
import { Reveal } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";
import { CTABand } from "@/components/CTABand";
import { EnquiryForm } from "@/components/EnquiryForm";
import { CoverageMap } from "@/components/CoverageMap";

const scrollToId = (id) => (e) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (!el) return;
    if (window.__lenis) window.__lenis.scrollTo(el, { offset: -88 });
    else el.scrollIntoView({ behavior: "smooth" });
};

const MaskedLine = ({ children, delay }) => (
    <span className="block overflow-hidden pb-1">
        <motion.span
            className="block"
            initial={{ y: "110%" }}
            animate={{ y: 0 }}
            transition={{ duration: 0.95, delay, ease: [0.22, 1, 0.36, 1] }}
        >
            {children}
        </motion.span>
    </span>
);

const Hero = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const yImg = useTransform(scrollYProgress, [0, 1], [0, 80]);

    return (
        <section ref={ref} data-testid="hero-section" className="relative overflow-hidden bg-slate-50">
            <div className="pointer-events-none absolute -right-40 -top-40 h-[28rem] w-[28rem] rounded-full bg-emerald-100/70 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-40 -left-40 h-[24rem] w-[24rem] rounded-full bg-slate-200/50 blur-3xl" />
            <div className="relative mx-auto grid max-w-7xl gap-14 px-4 pb-16 pt-14 sm:px-6 md:pb-24 md:pt-20 lg:grid-cols-[1.1fr_1fr] lg:items-center lg:px-8">
                <div>
                    <motion.span
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="inline-block rounded-full border border-emerald-200 bg-emerald-50 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.18em] text-emerald-700"
                    >
                        Air Source Heat Pump Specialists
                    </motion.span>
                    <h1 className="mt-6 font-display text-4xl font-extrabold leading-[1.08] tracking-tight text-slate-900 sm:text-5xl lg:text-6xl">
                        <MaskedLine delay={0.15}>Smarter Heating.</MaskedLine>
                        <MaskedLine delay={0.3}>
                            <span className="text-emerald-600">Cleaner Energy.</span>
                        </MaskedLine>
                    </h1>
                    <motion.p
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.5 }}
                        className="mt-6 text-lg font-semibold text-slate-700 sm:text-xl"
                    >
                        Professional air source heat pump solutions for homes across England &amp; Wales.
                    </motion.p>
                    <motion.p
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.62 }}
                        className="mt-3 max-w-xl text-base leading-relaxed text-slate-600"
                    >
                        Virtex Renewables helps homeowners and landlords transition to efficient, low-carbon heating
                        with air source heat pump systems designed around their property.
                    </motion.p>
                    <motion.div
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.74 }}
                        className="mt-8 flex flex-col gap-3 sm:flex-row"
                    >
                        <a
                            href="#assessment"
                            onClick={scrollToId("assessment")}
                            data-testid="hero-assessment-button"
                            className="group flex items-center justify-center gap-2 rounded-full bg-emerald-600 px-7 py-4 text-sm font-bold text-white shadow-xl shadow-emerald-600/25 transition-colors duration-200 hover:bg-emerald-700"
                        >
                            Get a Free Assessment
                            <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
                        </a>
                        <a
                            href={COMPANY.phoneHref}
                            data-testid="hero-call-button"
                            className="flex items-center justify-center gap-2 rounded-full border border-slate-300 bg-white px-7 py-4 text-sm font-bold text-slate-900 transition-colors duration-200 hover:border-emerald-600 hover:text-emerald-700"
                        >
                            <Phone className="h-4 w-4 text-emerald-600" /> Call {COMPANY.phoneDisplay}
                        </a>
                    </motion.div>
                    <motion.ul
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.8, delay: 0.9 }}
                        className="mt-9 grid gap-3 sm:grid-cols-3"
                        data-testid="hero-trust-points"
                    >
                        {["Professional Heat Pump Installations", "Property-Specific System Design", "Serving England & Wales"].map((point) => (
                            <li key={point} className="flex items-start gap-2 text-sm font-semibold text-slate-700">
                                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                                {point}
                            </li>
                        ))}
                    </motion.ul>
                </div>

                <motion.div
                    initial={{ opacity: 0, scale: 0.96 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.9, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
                    className="relative"
                >
                    <motion.div style={{ y: yImg }} className="relative">
                        <img
                            src={IMAGES.hero}
                            alt="Air source heat pump installed outside a modern UK brick residential property"
                            className="aspect-[4/3] w-full rounded-3xl object-cover shadow-2xl"
                            loading="eager"
                        />
                        <div className="absolute -bottom-6 left-5 right-5 rounded-2xl border border-slate-200 bg-white/95 p-5 shadow-xl backdrop-blur sm:right-auto sm:w-80">
                            <p className="text-xs font-bold uppercase tracking-[0.18em] text-emerald-700">Boiler Upgrade Scheme</p>
                            <p className="mt-1 font-display text-lg font-bold text-slate-900">Up to £7,500 grant available</p>
                            <p className="mt-1 text-xs text-slate-500">Subject to eligibility and current scheme rules.</p>
                        </div>
                    </motion.div>
                </motion.div>
            </div>
        </section>
    );
};

const SectionHeading = ({ eyebrow, title, text, center = false }) => (
    <Reveal className={center ? "mx-auto max-w-2xl text-center" : "max-w-2xl"}>
        {eyebrow && (
            <span className="inline-block rounded-full bg-emerald-50 px-3 py-1 text-xs font-bold uppercase tracking-[0.2em] text-emerald-700">
                {eyebrow}
            </span>
        )}
        <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">{title}</h2>
        {text && <p className="mt-4 text-base leading-relaxed text-slate-600">{text}</p>}
    </Reveal>
);

const BENEFITS = [
    { icon: Gauge, title: "Efficient Heating", text: "Heat pumps can deliver multiple units of heat energy for each unit of electricity used." },
    { icon: Droplets, title: "Heating & Hot Water", text: "A correctly designed system can provide both space heating and domestic hot water." },
    { icon: Leaf, title: "Lower-Carbon Technology", text: "Reduce reliance on fossil-fuel heating." },
    { icon: SunSnow, title: "Year-Round Operation", text: "Modern air source heat pumps are designed to operate throughout the UK heating season." },
];

const STEPS = [
    { n: "01", title: "Initial Enquiry", text: "Tell us about your property, existing heating system and requirements." },
    { n: "02", title: "Property Assessment", text: "The property and existing heating system are assessed to understand the requirements of the installation." },
    { n: "03", title: "Heat-Loss & System Design", text: "Heating requirements are calculated so the proposed heat pump and heating system can be appropriately designed." },
    { n: "04", title: "Installation", text: "The heat pump, hot-water cylinder and required heating-system components are installed according to the agreed specification." },
    { n: "05", title: "Commissioning & Handover", text: "The completed system is commissioned and the homeowner is shown how to operate their new heating system." },
];

const WHY = [
    { icon: Ruler, title: "Designed Around Your Property", text: "Every home is different. System design should take into account the property's heating requirements rather than relying solely on property size." },
    { icon: ClipboardCheck, title: "Clear Recommendations", text: "We explain the proposed heating system and identify any heating-system improvements that may be required." },
    { icon: Leaf, title: "Renewable Heating Focus", text: "Our focus is on modern air source heat pump technology and correctly designed low-temperature heating systems." },
    { icon: HeartHandshake, title: "Support Throughout the Process", text: "From the first enquiry through to installation and handover, customers have a clear journey through the process." },
];

const PROPERTY_TYPES = [
    { icon: HomeIcon, title: "Detached Homes" },
    { icon: House, title: "Semi-Detached Homes" },
    { icon: Building2, title: "Terraced Homes" },
    { icon: Warehouse, title: "Bungalows" },
    { icon: KeyRound, title: "Landlord Properties" },
    { icon: TreePine, title: "Rural & Off-Gas Homes" },
];

export default function Home() {
    return (
        <>
            <SEO
                title="Air Source Heat Pump Installers | Virtex Renewables"
                description="Virtex Renewables provides professional air source heat pump solutions for homeowners and landlords across England and Wales. Request a free property assessment today."
            />
            <Hero />
            <Marquee />

            <section className="bg-white py-16 md:py-24" data-testid="intro-section">
                <div className="mx-auto grid max-w-7xl gap-10 px-4 sm:px-6 lg:grid-cols-[1fr_1.2fr] lg:items-start lg:px-8">
                    <SectionHeading title="Renewable Heating Designed Around Your Home" />
                    <Reveal delay={0.1}>
                        <p className="text-base leading-relaxed text-slate-600">
                            At Virtex Renewables, we help homeowners and landlords make the transition to modern,
                            efficient heating.
                        </p>
                        <p className="mt-4 text-base leading-relaxed text-slate-600">
                            A successful heat pump installation starts with understanding the property. Our approach
                            considers heat loss, existing heating infrastructure, radiator requirements, hot-water
                            demand and the individual requirements of the home before the final system is specified.
                        </p>
                        <p className="mt-4 text-base leading-relaxed text-slate-600">
                            From the initial assessment through to installation and handover, we aim to make the
                            transition to renewable heating straightforward.
                        </p>
                        <a
                            href="#assessment"
                            onClick={scrollToId("assessment")}
                            data-testid="intro-assessment-button"
                            className="group mt-7 inline-flex items-center gap-2 rounded-full bg-slate-900 px-6 py-3.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-emerald-700"
                        >
                            Request a Free Assessment
                            <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
                        </a>
                    </Reveal>
                </div>
            </section>

            <section className="bg-slate-50 py-16 md:py-24" data-testid="heat-pump-section">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <div className="grid items-center gap-12 lg:grid-cols-2">
                        <Reveal>
                            <div className="relative">
                                <div className="absolute -left-4 -top-4 h-full w-full rounded-3xl border-2 border-emerald-200" aria-hidden="true" />
                                <img
                                    src={IMAGES.heatPumpUnit}
                                    alt="Modern air source heat pump outdoor unit installed against a UK brick home"
                                    className="relative aspect-[4/3] w-full rounded-3xl object-cover shadow-xl"
                                    loading="lazy"
                                />
                            </div>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <h2 className="font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
                                What Is an Air Source Heat Pump?
                            </h2>
                            <p className="mt-5 text-base leading-relaxed text-slate-600">
                                An air source heat pump extracts heat energy from the outside air and transfers it
                                into your home's heating and hot-water system.
                            </p>
                            <p className="mt-4 text-base leading-relaxed text-slate-600">
                                Unlike traditional gas and oil boilers, heat pumps do not generate heat through
                                on-site fuel combustion. Instead, they use electricity to transfer naturally
                                occurring heat from the outside air into your home.
                            </p>
                            <p className="mt-4 text-base leading-relaxed text-slate-600">
                                When properly designed for the property, an air source heat pump can provide an
                                efficient, low-carbon alternative to traditional fossil-fuel heating.
                            </p>
                            <Link
                                to="/heat-pumps"
                                data-testid="heat-pump-learn-more-button"
                                className="group mt-7 inline-flex items-center gap-2 rounded-full bg-emerald-600 px-6 py-3.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-emerald-700"
                            >
                                Learn About Heat Pumps
                                <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
                            </Link>
                        </Reveal>
                    </div>

                    <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
                        {BENEFITS.map((b, i) => (
                            <Reveal key={b.title} delay={i * 0.08}>
                                <div className="h-full rounded-2xl border border-slate-200 bg-white p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl" data-testid={`benefit-card-${i}`}>
                                    <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-50">
                                        <b.icon className="h-5 w-5 text-emerald-600" />
                                    </div>
                                    <h3 className="mt-4 font-display text-lg font-semibold text-slate-900">{b.title}</h3>
                                    <p className="mt-2 text-sm leading-relaxed text-slate-600">{b.text}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <section className="bg-white py-16 md:py-24" data-testid="bus-section">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <Reveal>
                        <div className="relative overflow-hidden rounded-3xl bg-slate-950 px-6 py-16 md:px-16 md:py-20">
                            <p className="pointer-events-none absolute -right-6 -top-10 select-none font-display text-[11rem] font-extrabold leading-none text-white/5 md:text-[16rem]" aria-hidden="true">
                                £7.5k
                            </p>
                            <div className="relative max-w-2xl">
                                <span className="inline-block rounded-full bg-emerald-500/15 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-emerald-400">
                                    Boiler Upgrade Scheme
                                </span>
                                <h2 className="mt-5 font-display text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl">
                                    Up to <span className="text-emerald-400">£7,500</span> Towards an Air Source Heat Pump
                                </h2>
                                <p className="mt-5 text-base leading-relaxed text-slate-300">
                                    The UK Government's Boiler Upgrade Scheme provides eligible property owners in
                                    England and Wales with a grant of up to <strong className="text-white">£7,500</strong> towards
                                    the cost of replacing an eligible fossil-fuel heating system with an air source
                                    heat pump.
                                </p>
                                <p className="mt-3 text-base leading-relaxed text-slate-400">
                                    Eligibility, property suitability and current scheme requirements apply.
                                </p>
                                <Link
                                    to="/boiler-upgrade-scheme"
                                    data-testid="bus-check-property-button"
                                    className="group mt-8 inline-flex items-center gap-2 rounded-full bg-emerald-600 px-7 py-3.5 text-sm font-bold text-white transition-colors duration-200 hover:bg-emerald-500"
                                >
                                    Check Your Property
                                    <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
                                </Link>
                                <p className="mt-5 text-xs italic text-slate-500">
                                    Boiler Upgrade Scheme funding is subject to eligibility and current scheme rules.
                                </p>
                            </div>
                        </div>
                    </Reveal>
                </div>
            </section>

            <section className="bg-slate-50 py-16 md:py-24" data-testid="how-it-works-section">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <SectionHeading center eyebrow="The Process" title="Your Journey to Renewable Heating" />
                    <div className="mt-14 grid gap-5 md:grid-cols-2 lg:grid-cols-5">
                        {STEPS.map((s, i) => (
                            <Reveal key={s.n} delay={i * 0.08}>
                                <div className="relative h-full rounded-2xl border border-slate-200 bg-white p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl" data-testid={`step-card-${i}`}>
                                    <span className="font-display text-4xl font-extrabold text-emerald-600/90">{s.n}</span>
                                    <h3 className="mt-4 font-display text-lg font-semibold text-slate-900">{s.title}</h3>
                                    <p className="mt-2 text-sm leading-relaxed text-slate-600">{s.text}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <section className="bg-white py-16 md:py-24" data-testid="why-virtex-section">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <SectionHeading center eyebrow="Why Virtex Renewables" title="A Better Approach to Heat Pump Installation" />
                    <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
                        {WHY.map((w, i) => (
                            <Reveal key={w.title} delay={i * 0.08}>
                                <div className="h-full rounded-2xl border border-slate-200 bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:border-emerald-200 hover:shadow-xl" data-testid={`why-card-${i}`}>
                                    <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-600 shadow-lg shadow-emerald-600/25">
                                        <w.icon className="h-5 w-5 text-white" />
                                    </div>
                                    <h3 className="mt-5 font-display text-lg font-semibold text-slate-900">{w.title}</h3>
                                    <p className="mt-2.5 text-sm leading-relaxed text-slate-600">{w.text}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <section className="bg-slate-50 py-16 md:py-24" data-testid="suitability-section">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <SectionHeading
                        center
                        eyebrow="Property Suitability"
                        title="Could Your Home Be Suitable?"
                        text="Air source heat pumps can work with many different types of homes. The appropriate system depends on factors including heat loss, insulation, property size, radiator capacity, hot-water requirements and the existing heating system."
                    />
                    <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                        {PROPERTY_TYPES.map((p, i) => (
                            <Reveal key={p.title} delay={i * 0.06}>
                                <div className="flex h-full items-center gap-4 rounded-2xl border border-slate-200 bg-white p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl" data-testid={`property-card-${i}`}>
                                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-emerald-50">
                                        <p.icon className="h-5 w-5 text-emerald-600" />
                                    </div>
                                    <h3 className="font-display text-lg font-semibold text-slate-900">{p.title}</h3>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                    <Reveal className="mt-10 text-center">
                        <a
                            href="#assessment"
                            onClick={scrollToId("assessment")}
                            data-testid="suitability-check-button"
                            className="group inline-flex items-center gap-2 rounded-full bg-slate-900 px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-emerald-700"
                        >
                            Check My Property
                            <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
                        </a>
                    </Reveal>
                </div>
            </section>

            <section className="bg-white py-16 md:py-24" data-testid="coverage-section">
                <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
                    <Reveal>
                        <SectionHeading
                            eyebrow="Coverage"
                            title="Heat Pump Installations Across England & Wales"
                            text="Virtex Renewables provides air source heat pump services to homeowners and landlords across England and Wales."
                        />
                        <a
                            href="#assessment"
                            onClick={scrollToId("assessment")}
                            data-testid="coverage-postcode-button"
                            className="group mt-7 inline-flex items-center gap-2 rounded-full bg-emerald-600 px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-emerald-700"
                        >
                            Check Your Postcode
                            <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
                        </a>
                    </Reveal>
                    <Reveal delay={0.12}>
                        <div className="rounded-3xl border border-slate-200 bg-slate-50 p-6 md:p-9">
                            <CoverageMap />
                        </div>
                    </Reveal>
                </div>
            </section>

            <section className="bg-slate-50 py-16 md:py-24" data-testid="about-section">
                <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
                    <Reveal className="order-2 lg:order-1">
                        <h2 className="font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
                            About Virtex Renewables
                        </h2>
                        <p className="mt-5 text-base font-semibold leading-relaxed text-slate-800">
                            Virtex Renewables Ltd is a renewable heating company specialising in air source heat pump
                            systems for residential properties across England and Wales.
                        </p>
                        <p className="mt-4 text-base leading-relaxed text-slate-600">
                            We believe the performance of a heat pump starts with good system design. Our approach
                            considers the individual property, heat-loss requirements, existing heating system, heat
                            emitters and hot-water requirements before the final system specification is confirmed.
                        </p>
                        <p className="mt-4 text-base leading-relaxed text-slate-600">
                            Our aim is to provide homeowners and landlords with clear information, professional service
                            and renewable heating solutions designed around their property.
                        </p>
                        <Link
                            to="/about-us"
                            data-testid="about-more-button"
                            className="group mt-7 inline-flex items-center gap-2 rounded-full border border-slate-300 px-6 py-3.5 text-sm font-semibold text-slate-900 transition-colors duration-200 hover:border-emerald-600 hover:text-emerald-700"
                        >
                            More About Virtex Renewables
                            <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
                        </Link>
                    </Reveal>
                    <Reveal delay={0.12} className="order-1 lg:order-2">
                        <img
                            src={IMAGES.country}
                            alt="Traditional UK brick country home suited to an air source heat pump installation"
                            className="aspect-[4/3] w-full rounded-3xl object-cover shadow-xl"
                            loading="lazy"
                        />
                    </Reveal>
                </div>
            </section>

            <CTABand primaryTo="/#assessment" />

            <section id="assessment" className="scroll-mt-24 bg-slate-50 py-16 md:py-24" data-testid="assessment-section">
                <div className="mx-auto grid max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-[1fr_1.25fr] lg:items-start lg:px-8">
                    <Reveal>
                        <span className="inline-block rounded-full bg-emerald-50 px-3 py-1 text-xs font-bold uppercase tracking-[0.2em] text-emerald-700">
                            Free Property Assessment
                        </span>
                        <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
                            Request Your Free Heat Pump Assessment
                        </h2>
                        <p className="mt-4 text-base leading-relaxed text-slate-600">
                            Tell us a little about your property and our team can discuss your heating requirements
                            and the next steps.
                        </p>
                        <ul className="mt-8 space-y-4">
                            {[
                                "We review your property details and current heating system.",
                                "We discuss suitability, system design and available grant routes.",
                                "You receive clear recommendations — with no obligation.",
                            ].map((item) => (
                                <li key={item} className="flex items-start gap-3 text-sm font-medium leading-relaxed text-slate-700">
                                    <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
                                    {item}
                                </li>
                            ))}
                        </ul>
                        <p className="mt-8 text-sm text-slate-600">
                            Prefer to talk? Call us on{" "}
                            <a href={COMPANY.phoneHref} className="font-bold text-emerald-700 hover:underline" data-testid="assessment-phone-link">
                                {COMPANY.phoneDisplay}
                            </a>
                        </p>
                    </Reveal>
                    <Reveal delay={0.12}>
                        <EnquiryForm testIdPrefix="enquiry" />
                    </Reveal>
                </div>
            </section>
        </>
    );
}
